import React, { useState, useEffect } from "react";
import { callBackend } from "../utils/api";
import { X, Send } from "lucide-react";
import { log } from "../utils/logger";

export default function DirectChat({ friend, onClose, currentUser, socket }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");

  useEffect(() => {
    // Fetch history
    callBackend(`/social/messages/${friend.id}`).then(setMessages);

    // Mark as read immediately when opening
    callBackend(`/social/messages/read/${friend.id}`, { method: "POST" });

    // Listen for real-time DMs
    if (socket) {
      const handleNewDM = (msg) => {
        // Only add message if it belongs to this conversation
        if (msg.sender_id === friend.id || msg.sender_id === currentUser.id) {
          setMessages((prev) => [...prev, msg]);
        }
      };

      socket.on("new_dm", handleNewDM);
      return () => socket.off("new_dm", handleNewDM);
    }
  }, [friend.id, currentUser.id, socket]);

  const sendMessage = async () => {
    if (!text.trim()) return;
    try {
      await callBackend("/social/message", {
        method: "POST",
        body: { receiverId: friend.id, message: text },
      });
      setText("");
    } catch (err) {
      log("DIRECT_CHAT", "Message failed", err);
    }
  };

  return (
    <div className="bg-zinc-950 border-2 border-blue-600 p-6 shadow-2xl font-mono h-[500px] flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center border-b border-blue-900 pb-4 mb-4">
        <h3 className="text-xl font-black text-blue-500 uppercase italic">
          Pager: {friend.username}
        </h3>
        <button
          onClick={onClose}
          aria-label="Close pager"
          className="text-zinc-500 hover:text-white"
        >
          <X size={20} />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 mb-4 scrollbar-hide">
        {messages.map((m) => (
          <div
            key={m.id}
            className={`flex ${
              m.sender_id === currentUser.id ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`max-w-[80%] p-2 px-3 text-xs font-bold border ${
                m.sender_id === currentUser.id
                  ? "bg-zinc-900 border-zinc-800 text-white"
                  : "bg-blue-900/20 border-blue-900 text-blue-300"
              }`}
            >
              {m.message}
            </div>
          </div>
        ))}
      </div>

      {/* Input */}
      <div className="flex gap-2 bg-black border border-zinc-800 p-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          placeholder="SEND ENCRYPTED PAGE..."
          className="flex-1 bg-transparent text-[10px] text-white outline-none uppercase"
        />
        <button
          onClick={sendMessage}
          aria-label="Send pager message"
          className="text-blue-500 hover:text-white transition-colors"
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}
